/**
 * PostHog host resolution — single source of truth shared by the build-time
 * reverse-proxy rewrites (next.config.ts) and the browser SDK init
 * (lib/analytics.ts).
 *
 * Why a reverse proxy at all: PostHog session replay loads its rrweb recorder
 * from `*-assets.i.posthog.com` and streams snapshots to `POST /s/` on the
 * ingest host. Both are third-party requests, so privacy extensions and
 * network-level blockers drop them — which is exactly what produces the
 * "The first N minutes of this recording can't be shown" warning (events
 * arrive but the initial DOM snapshot never does) and mid-session truncation
 * (later snapshot batches silently dropped). Routing everything through a
 * first-party path (`/ingest/*`, rewritten server-side to PostHog) makes the
 * requests same-origin and survives blockers. This is PostHog's documented
 * production setup: https://posthog.com/docs/advanced/proxy/nextjs
 *
 * Pure string math, zero imports — safe to import from next.config.ts (Node,
 * build time) and from client code (inlined at build time).
 */

/**
 * Same-origin path the browser SDK posts to (`api_host`). Rewritten to PostHog
 * by next.config.ts. MUST stay in sync with the rewrite `source` prefix there.
 */
export const POSTHOG_PROXY_PATH = "/ingest";

/**
 * Default ingest host when NEXT_PUBLIC_POSTHOG_HOST is unset. EU — matches
 * .env.example, the preview-replay injector, and the configured project's
 * region. (In practice the env var is always set; this is only a safety net.)
 */
export const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";

export interface PostHogHosts {
  /** Absolute ingest host (events / snapshots / flags). Rewrite destination. */
  ingestHost: string;
  /** Absolute static-asset host serving the rrweb recorder + array bundle. */
  assetsHost: string;
  /** PostHog app host for UI deep-links (get_session_replay_url, toolbar). */
  uiHost: string;
}

/** Drop a single trailing slash so host + path concatenation stays clean. */
function stripTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

/**
 * Derive the ingest / assets / UI hosts from the configured ingest host.
 *
 * For PostHog Cloud (`<region>.i.posthog.com`) this mirrors PostHog's own
 * transforms: assets live at `<region>-assets.i.posthog.com` and the app UI at
 * `<region>.posthog.com`. For a self-hosted / custom host (no `.i.posthog.com`)
 * all three collapse to that host, which is how self-hosted instances serve
 * assets and UI.
 */
export function resolvePostHogHosts(rawHost?: string | null): PostHogHosts {
  const ingestHost = stripTrailingSlash(
    (rawHost ?? "").trim() || DEFAULT_POSTHOG_HOST,
  );
  const isCloud = ingestHost.includes(".i.posthog.com");
  return {
    ingestHost,
    assetsHost: isCloud
      ? ingestHost.replace(".i.posthog.com", "-assets.i.posthog.com")
      : ingestHost,
    uiHost: isCloud
      ? ingestHost.replace(".i.posthog.com", ".posthog.com")
      : ingestHost,
  };
}
